import { compareDesc, formatISO, parseISO } from 'date-fns';

import { useMutation } from '@tanstack/react-query';

import { getEnvRequired } from '../../shared/utilities/env';

import { DossierId } from '../dossier/types';
import { ApiClient, HttpError } from '../shared/api/client';

import {
    serializeDiscussionFromAPI,
    serializeMessageAttachmentFromApiV2,
    serializeMessageFromAPI,
    serializeScheduledMessageFromAPI,
} from './serializers';
import {
    GetOneMessageAPIResponse,
    SendMessageAPIResponse,
    DiscussionAPIResponse,
    UploadAttachmentAPI,
    ScheduleMessageAPIResponse,
    ScheduledMessagesAPIResponse,
    GetDraftMessageApiResponse,
    GetDiscussionAttachmentsAPIResponse,
    MessageAttachmentFromAPI,
    MessageAttachment,
    NoResponseRequiredReason,
} from './types';

const DISCUSSION_API_URL = getEnvRequired('DISCUSSION_API_URL');
const APIV2_URL = getEnvRequired('APIV2_URL');

const apiClient = new ApiClient(false);

export async function getDiscussion(dossierId: DossierId, page = 1) {
    const url = `${DISCUSSION_API_URL}/discussions/${dossierId}/messages?page=${page}`;
    const response = await apiClient.get<DiscussionAPIResponse>(url);
    return serializeDiscussionFromAPI(response, dossierId);
}

export async function sendMessage(
    message: string,
    dossierId: DossierId,
    attachmentsIds: string[] = [],
    messageHtml?: string,
) {
    const url = `${DISCUSSION_API_URL}/discussions/${dossierId}.at/messages`;
    const formData = new window.FormData();
    if (messageHtml) {
        formData.append('contentHtml', messageHtml);
    }
    formData.append('content', message);
    formData.append('provenance', 'request-manager');
    attachmentsIds.forEach((attachmentId) => {
        formData.append('attachments[][id]', attachmentId);
    });
    return await apiClient.postForm<SendMessageAPIResponse>(url, formData);
}

export async function getOneMessage(messageId: number) {
    const url = `${DISCUSSION_API_URL}/discussions/messages/${messageId}/get`;
    const response = await apiClient.get<GetOneMessageAPIResponse>(url);
    return serializeMessageFromAPI(response.message);
}

export async function uploadAttachment(file: File, category: string | null) {
    const url = `${DISCUSSION_API_URL}/discussions/upload`;
    const formData = new window.FormData();
    formData.append('file', file);
    if (category) formData.append('category', category ?? '');
    return await apiClient.postForm<UploadAttachmentAPI>(url, formData);
}

export async function scheduleMessage({
    dossierId,
    content,
    contentHtml,
    scheduledAt,
    attachmentIds = [],
}: {
    dossierId: DossierId;
    content: string;
    contentHtml: string;
    scheduledAt: Date;
    attachmentIds?: string[];
}) {
    const url = `${DISCUSSION_API_URL}/discussions/${dossierId}.at/scheduled-messages`;

    const formData = new window.FormData();

    formData.append('content', content);
    formData.append('contentHtml', contentHtml);
    formData.append('provenance', 'request-manager');
    formData.append('scheduledAt', formatISO(scheduledAt));
    attachmentIds.forEach((attachmentId) => {
        formData.append('attachments[][id]', attachmentId);
    });

    return await apiClient.postForm<ScheduleMessageAPIResponse>(url, formData);
}

export async function getScheduledMessages(dossierId: DossierId) {
    const url = `${DISCUSSION_API_URL}/discussions/${dossierId}.at/scheduled-messages`;
    const response = await apiClient.get<ScheduledMessagesAPIResponse[]>(url);
    if (response.length === 0) {
        return null;
    }
    const [followUpScheduledMessage] = response;
    return serializeScheduledMessageFromAPI(followUpScheduledMessage);
}

export async function deleteScheduledMessage(dossierId: DossierId, scheduledMessageId: string) {
    const url = `${DISCUSSION_API_URL}/discussions/${dossierId}.at/scheduled-messages/${scheduledMessageId}`;

    await apiClient.delete(url);
}

export async function markMessageAsRead(messageId: number) {
    const url = `${DISCUSSION_API_URL}/discussions/messages/${messageId}/mark-as-read`;

    await apiClient.post(url);
}

export async function markAsNoResponseRequired(
    dossierId: DossierId,
    messageId: number | null,
    reason: NoResponseRequiredReason = 'no_response_required',
) {
    const url = `${DISCUSSION_API_URL}/discussions/${dossierId}/mark-as-no-response-required`;

    if (!messageId) {
        return await apiClient.post<void>(url);
    }

    return await apiClient.post<void>(url, JSON.stringify({ messageId, reason }));
}

export async function markAsResponseRequired(dossierId: DossierId) {
    const url = `${DISCUSSION_API_URL}/discussions/${dossierId}/mark-as-response-required`;

    await apiClient.post(url);
}

export async function getDraftMessage(dossierId: DossierId) {
    const url = `${DISCUSSION_API_URL}/discussions/${dossierId}.at/draft-messages`;

    const response = await apiClient.get<GetDraftMessageApiResponse[]>(url);
    if (response.length === 0) {
        return { content: '', contentHtml: '' };
    }

    const latestDraftMessage = response
        .map((draftMessageJson: GetDraftMessageApiResponse) => ({
            updatedAt: parseISO(draftMessageJson.updatedAt),
            content: draftMessageJson.content,
            contentHtml: draftMessageJson.contentHtml,
        }))
        .sort((a, b) => compareDesc(a.updatedAt, b.updatedAt))
        .at(0);

    return {
        content: latestDraftMessage?.content || '',
        contentHtml: latestDraftMessage?.contentHtml || '',
    };
}

export async function putDraftMessage(
    dossierId: DossierId,
    draftMessage: string,
    htmlDraftMessage: string,
) {
    const url = `${DISCUSSION_API_URL}/discussions/${dossierId}.at/draft-messages`;

    const formData = new window.FormData();
    formData.append('content', draftMessage);
    formData.append('contentHtml', htmlDraftMessage);
    formData.append('provenance', 'request-manager');

    await apiClient.postForm(url, formData);
}

export async function getDiscussionAttachments(dossierId: DossierId) {
    const result: MessageAttachmentFromAPI[] = [];
    let response: GetDiscussionAttachmentsAPIResponse;
    let pageNumber = 1;
    do {
        response = await apiClient.get(
            `${APIV2_URL}/discussions/${dossierId}/attachments?page=${pageNumber++}`,
        );
        result.push(...response.data);
    } while (pageNumber <= response.pagination.total_pages);

    return result.map(
        (attachmentFromAPI): MessageAttachment =>
            serializeMessageAttachmentFromApiV2(attachmentFromAPI),
    );
}

type EditMessageRequest = {
    dossierId: DossierId;
    messageId: string | number;
    content: string;
    contentHtml: string;
    attachmentsIds?: number[];
};

export const useEditMessageMutation = () => {
    return useMutation<void, HttpError, EditMessageRequest>({
        mutationFn: async ({ dossierId, messageId, content, contentHtml, attachmentsIds = [] }) => {
            const url = `${DISCUSSION_API_URL}/discussions/${dossierId}.at/messages/${messageId}`;
            const headers = {
                'Content-Type': 'application/json',
            };
            return await apiClient.put<void>(
                url,
                JSON.stringify({
                    content,
                    contentHtml,
                    provenance: 'request-manager',
                    attachments: attachmentsIds.map((attachmentId) => ({ id: attachmentId })),
                }),
                headers,
            );
        },
    });
};

type EditScheduledMessageRequest = {
    dossierId: DossierId;
    scheduledMessageId: string | number;
    content: string;
    contentHtml: string;
    attachmentsIds?: number[];
};

export const useEditScheduledMessageMutation = () => {
    return useMutation<void, HttpError, EditScheduledMessageRequest>({
        mutationFn: async ({
            dossierId,
            scheduledMessageId,
            content,
            contentHtml,
            attachmentsIds = [],
        }) => {
            const url = `${DISCUSSION_API_URL}/discussions/${dossierId}.at/scheduled-messages/${scheduledMessageId}`;
            const headers = {
                'Content-Type': 'application/json',
            };
            return await apiClient.put<void>(
                url,
                JSON.stringify({
                    content,
                    contentHtml,
                    attachments: attachmentsIds.map((attachmentId) => ({ id: attachmentId })),
                }),
                headers,
            );
        },
    });
};
